import Flex from 'components/common/Flex';
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
// import cloudUpload from 'assets/img/icons/cloud-upload.svg';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import CardDropdown from 'components/common/CardDropdown';
import { AuthUpload } from 'helpers/api/AuthCRUD';
import { API } from 'helpers/api/endpoints';
import { getSize } from 'helpers/utils';
import { Accordion, Button, Dropdown, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AiOutlineCloudUpload } from 'react-icons/ai';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import * as yup from 'yup';
import CustomerDropdowns from './CustomerDropdowns';
import LicenceTypeDropdown from './LicenceTypeDropdown';
import ProductDropdown from './ProductDropdown';
type Props = {
  refetch: () => void;
};

export type backendProductTypes =
  | 'FlowsterStudio'
  | 'FSM'
  | 'PasswordResetPortal'
  | 'FlowsterAIStudio';

export const ALLOWED_PRODUCTS: readonly backendProductTypes[] = (
  [
    'FSM',
    'FlowsterStudio',
    'PasswordResetPortal',
    'FlowsterAIStudio',
  ] as backendProductTypes[]
).sort((a, b) => a.localeCompare(b));

const SingelFileUpload = ({ refetch }: Props) => {
  const { t } = useTranslation('licenses');
  const { token } = useSelector((state: RootState) => state);

  const [productType, setProductType] = useState<backendProductTypes>(
    ALLOWED_PRODUCTS[0]
  );
  const [licenseType, setLicenseType] = useState<string>('');
  const [customerGuid, setCustomerGuid] = useState<string | null>(null);

  // Flowster AI Studio licences are pasted as a signed JWT token instead of a file.
  const isAIStudio = productType === 'FlowsterAIStudio';
  const [jwtToken, setJwtToken] = useState<string>('');

  const schema = yup
    .object({
      uploadedFiles: yup
        .array()
        .required('This field is required.')
        .min(1, 'This field is required.'),
    })
    .required();
  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    clearErrors,
  } = useForm({
    resolver: yupResolver(schema),
  });
  const files: File[] = watch('uploadedFiles') ?? [];

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'application/x-iwork-keynote-sffkey': ['.key'],
    },
    onDrop: (acceptedFiles) => {
      if (errors.uploadedFiles) {
        clearErrors('uploadedFiles');
      }
      setValue('uploadedFiles', acceptedFiles);
    },
  });
  const handleRemove = (name: string) => {
    setValue(
      'uploadedFiles',
      files.filter((file) => file.name !== name)
    );
  };
  // Sends a FormData "file" to the upload endpoint. The backend reads the part
  // as text, so the AI Studio JWT can be submitted as an in-memory file.
  const uploadFormData = async (formData: FormData) => {
    if (!token || !customerGuid) return;

    // licence_type may be empty (e.g. Flowster AI Studio — plan is part of the signed key)
    const url = API.licence.upload(
      productType,
      customerGuid,
      encodeURIComponent(licenseType.trim())
    );
    return AuthUpload(url, formData, token);
  };

  type SubmitProps = {
    uploadedFiles: File[];
  };
  const onSubmit = async (data: SubmitProps) => {
    if (!token || !data || !customerGuid) return;
    const formData = new FormData();
    data.uploadedFiles.forEach((file) => {
      formData.append('uploadedFiles', file);
    });

    const response = await uploadFormData(formData);
    if (response === 'success') {
      setValue('uploadedFiles', []);
      refetch();
    }
  };

  const onSubmitToken = async () => {
    const trimmed = jwtToken.trim();
    if (!token || !customerGuid || !trimmed) return;

    const formData = new FormData();
    const tokenFile = new File([trimmed], 'Flowster_AI_Studio.jwt', {
      type: 'text/plain',
    });
    formData.append('uploadedFiles', tokenFile);

    const response = await uploadFormData(formData);
    if (response === 'success') {
      setJwtToken('');
      refetch();
    }
  };

  return (
    <Accordion>
      <Accordion.Item eventKey="0">
        <Accordion.Header>{t('Upload')}</Accordion.Header>
        <Accordion.Body>
          <CustomerDropdowns setSelectedCustomer={setCustomerGuid} />
          <ProductDropdown setProductType={setProductType} />
          <LicenceTypeDropdown
            software_type={productType}
            setLicenseType={setLicenseType}
          />

          {isAIStudio ? (
            <Form
              className="mt-5"
              onSubmit={(e) => {
                e.preventDefault();
                onSubmitToken();
              }}
            >
              <Form.Label className="mb-1 fs--1">{t('jwt_token')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                value={jwtToken}
                onChange={(e) => setJwtToken(e.target.value)}
                placeholder={t('paste_jwt_token') ?? ''}
                className="font-monospace"
                style={{ wordBreak: 'break-all' }}
              />
              <br />
              <Button
                disabled={!customerGuid || !jwtToken.trim()}
                variant="primary"
                type="submit"
                className="me-2"
                size="sm"
              >
                {t('Submit')}
              </Button>
            </Form>
          ) : (
            <Form className="mt-5 " onSubmit={handleSubmit(onSubmit)}>
              <div
                {...getRootProps({
                  className: `${classNames('dropzone-area py-6', {
                    'is-invalid': errors.uploadedFiles,
                  })}`,
                })}
              >
                <input
                  name="uploadedFiles"
                  {...getInputProps({ multiple: false })}
                />
                <Flex justifyContent="center" alignItems="center">
                  {/* <img src={cloudUpload} alt="" width={25} className="me-2" /> */}
                  <AiOutlineCloudUpload className="me-2 display-4" />

                  <p className="fs-0 mb-0 text-700">
                    {t('drop_licence_download')}
                  </p>
                </Flex>
              </div>
              <div className="invalid-feedback">
                {errors.uploadedFiles?.message}
              </div>
              <div>
                {files.map((file) => (
                  <Flex
                    alignItems="center"
                    className="py-3 border-bottom btn-reveal-trigger"
                    key={file.name}
                  >
                    {/* <img
              className="rounded"
              width={40}
              height={40}
              src={file.preview}
              alt={file.name}
            /> */}
                    <Flex
                      justifyContent="between"
                      alignItems="center"
                      className="ms-3 flex-1"
                    >
                      <div>
                        <h6>{file.name}</h6>
                        <Flex className="position-relative" alignItems="center">
                          <p className="mb-0 fs--1 text-400 line-height-1">
                            <strong>{getSize(file.size)}</strong>
                          </p>
                        </Flex>
                      </div>
                    </Flex>

                    <CardDropdown>
                      <div className="py-2">
                        <Dropdown.Item
                          className="text-danger"
                          onClick={() => handleRemove(file.name)}
                        >
                          {t('Remove')}
                        </Dropdown.Item>
                      </div>
                    </CardDropdown>
                  </Flex>
                ))}
              </div>
              <br />
              <Button
                disabled={!customerGuid}
                variant="primary"
                type="submit"
                className="me-2"
                size="sm"
              >
                {t('Submit')}
              </Button>
            </Form>
          )}
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default SingelFileUpload;
